import { addBreadcrumb, captureException } from "@sentry/react"
import {
  PublicClientApplication,
  LogLevel,
  EventType,
  InteractionRequiredAuthError,
  BrowserAuthError,
  BrowserAuthErrorCodes,
} from "@azure/msal-browser"
import type {
  ILoggerCallback,
  RedirectRequest,
  EndSessionRequest,
  EventMessage,
  AuthenticationResult,
} from "@azure/msal-browser"
import { logError, logWarning } from "@/v2-ui/utils/utils.log"
import config from "@/v2-backoffice-client/config"
import { setUser } from "@/v2-backoffice-client/sentry"

const {
  azAuthAuthority,
  azAuthClientId,
  azAuthRedirectUrl,
  azAuthPostLogoutRedirectUri,
  isProd,
} = config

// msal's own log lines stay OUT of Sentry issues. msal 5 ships obfuscated
// message codes ("Warning - 1kl318", "Error - 0yfud8") and logs every failure
// it is about to THROW as well — the thrown error is what our code handles
// and reports, with context. Forwarding the lines as events made msal's
// routine warnings the largest "issue" of the week (V1-CONSOLE-7PK: 507
// users, 1,590 events) and buried the real defects under <unknown> issues.
// They ride along as breadcrumbs on whatever real error follows instead.
const loggerCallback: ILoggerCallback = (level, msg, _hasPii) => {
  if (level !== LogLevel.Warning && level !== LogLevel.Error) return
  addBreadcrumb({
    category: "msal",
    level: level === LogLevel.Error ? "error" : "warning",
    message: msg,
  })
  if (!isProd) {
    // eslint-disable-next-line no-console
    console.warn(msg)
  }
}

export const instance = new PublicClientApplication({
  auth: {
    clientId: azAuthClientId,
    authority: azAuthAuthority,
    redirectUri: azAuthRedirectUrl,
    postLogoutRedirectUri: azAuthPostLogoutRedirectUri,
  },
  cache: {
    cacheLocation: "localStorage",
  },
  system: {
    loggerOptions: {
      loggerCallback,
    },
  },
})

export let idToken: string | null = null

export const msalReady = instance
  .initialize()
  .then(() => {
    const accounts = instance.getAllAccounts()
    if (accounts.length > 0) {
      instance.setActiveAccount(accounts[0])
      // Rehydrate Sentry user on reload — without this, the first
      // errors of the session land on Sentry without a user id.
      setUser(accounts[0])
    }

    return instance.handleRedirectPromise()
  })
  .then((res) => {
    if (res?.idToken) {
      idToken = res.idToken
      instance.setActiveAccount(res.account)
      setUser(res.account)
    }
  })

instance.addEventCallback((e: EventMessage) => {
  if (
    e.eventType === EventType.LOGIN_SUCCESS ||
    e.eventType === EventType.ACQUIRE_TOKEN_SUCCESS
  ) {
    const d = e.payload as AuthenticationResult
    idToken = d.idToken
    instance.setActiveAccount(d.account)
    setUser(d.account)
  }

  if (e.eventType === EventType.LOGOUT_START) {
    instance.setActiveAccount(null)
    idToken = null
    setUser(null)
  }

  if (e.error) {
    onAuthError(e)
  }
})

// MSAL transport failures: the token request never got a usable response
// (laptop asleep, network down, Entra unreachable). The 5-minute AuthLoader
// refresh and the per-request authLink retry these continuously, so one
// offline evening with a backgrounded tab produced hundreds of identical
// Sentry events (V2-BACKOFFICE-CLIENT-2/-Y/-13 — exemplar events all tagged
// document.hidden, e.g. at 03:08 at night). They're self-healing: the next
// refresh with network back succeeds, and a genuinely stale token is
// recovered by the 401 error-link path (apollo.client.ts). Only report them
// when the tab is visible AND the browser thinks it's online — that
// remainder is a real "Entra unreachable while someone is working" signal
// (FUS-900).
const MSAL_TRANSPORT_ERROR_CODES = new Set<string>([
  BrowserAuthErrorCodes.postRequestFailed,
  BrowserAuthErrorCodes.noNetworkConnectivity,
])

function isBackgroundTransportError(error: unknown): boolean {
  if (!(error instanceof BrowserAuthError)) return false
  if (!MSAL_TRANSPORT_ERROR_CODES.has(error.errorCode)) return false
  return document.hidden || !navigator.onLine
}

function onAuthError(e: EventMessage) {
  if (e.error instanceof InteractionRequiredAuthError) {
    // eslint-disable-next-line no-console
    console.debug(
      "Interactive authentication required — will be handled on next API call",
    )
    return
  }

  if (isBackgroundTransportError(e.error)) {
    // eslint-disable-next-line no-console
    console.debug(
      "MSAL transport failure in a background/offline tab — will retry",
      e.error,
    )
    return
  }

  // The hidden-iframe renewal timed out (redirect_bridge_timeout). The
  // 3 Sep bridge on /auth.html took this from dozens a day to a handful a
  // month (V2-BACKOFFICE-CLIENT-3K); what is left is a frame the browser
  // never let finish (tracking prevention, a slow link). It is recovered
  // in full by the 401 path in apollo.client.ts, which refreshes again and
  // otherwise sends the person through the interactive login — so it is a
  // warning to keep an eye on, not an error issue per occurrence.
  if (
    e.error instanceof BrowserAuthError &&
    e.error.errorCode === BrowserAuthErrorCodes.timedOut
  ) {
    logWarning("MSAL silent renewal timed out; the 401 path recovers", {
      level: "warning",
      extra: { subError: e.error.subError, interactionType: e.interactionType },
    })
    return
  }

  if (!isProd) {
    logError(e.error)
    return
  }

  captureException(e.error, {
    contexts: {
      Auth: {
        eventType: e.eventType,
        interactionType: e.interactionType,
        payload: e.payload,
        timestamp: e.timestamp,
      },
    },
  })
}

const scopes = ["openid", "profile"]

// When the refresh token has expired (24h for SPAs), MSAL falls back to a
// hidden-iframe renewal that navigates the iframe to `redirectUri`. That
// must point at a dedicated static reply page: with the default "/" the
// whole SPA boots inside the iframe, react-router rewrites the URL before
// MSAL can read the auth response, the silent renewal times out, and we end
// up doing a full-page login redirect — i.e. "the app reloads when the token
// expires". We reuse the single /auth.html reply URL (azAuthRedirectUrl),
// which inside a frame runs msal v5's redirect bridge (the frame must POST
// the response on a BroadcastChannel; an inert page times out —
// V2-BACKOFFICE-CLIENT-3K). Same setup as v2-console.
//
// It MUST be absolute. The interactive redirect flow resolves a relative
// URI against the current origin before navigating, but the silent
// refresh-token flow (RefreshTokenClient) forwards `redirectUri` to the
// token endpoint verbatim — and Azure AD rejects a relative value with
// AADSTS500116 ("The reply uri specified in the request is not a valid
// URL"), failing every silent renewal (V2-BACKOFFICE-CLIENT-1A).
const SILENT_REDIRECT_URI = `${window.location.origin}${azAuthRedirectUrl}`

const loginRequest: RedirectRequest = {
  scopes,
  redirectUri: azAuthRedirectUrl,
}

export async function login(request: Partial<RedirectRequest> = {}) {
  await msalReady
  return instance.loginRedirect({
    ...loginRequest,
    ...request,
  })
}

export function logout() {
  // The active account is resolved HERE, not at module scope: msal-browser
  // v5 throws uninitialized_public_client_application on ANY account access
  // before initialize() resolves, and module evaluation runs long before
  // msalReady settles — a top-level getActiveAccount() killed the whole
  // bundle at import (blank page, Aug 2026). v3 tolerated it by returning
  // null, which is how the original module-scope logoutRequest survived.
  const logoutRequest: EndSessionRequest = {
    account: instance.getActiveAccount() ?? undefined,
  }
  instance.logoutRedirect({
    ...logoutRequest,
    postLogoutRedirectUri: azAuthPostLogoutRedirectUri,
  })
}

export type AcquireTokenSilentOptions = {
  forceRefresh?: boolean
}

export async function acquireTokenSilent(
  options: AcquireTokenSilentOptions = {},
) {
  await msalReady

  const { forceRefresh = false } = options

  const account = instance.getActiveAccount()
  if (!account) {
    throw new Error("No active account")
  }

  const response = await instance.acquireTokenSilent({
    scopes,
    account,
    forceRefresh,
    redirectUri: SILENT_REDIRECT_URI,
  })

  if (response.idToken) {
    idToken = response.idToken
  }

  return response
}
